<script setup lang="ts">
import { useDialog } from 'primevue/usedialog'
import { ProductType } from '~/lib/services/store/catalog/catalog.dtos'
import { ProductLiteWithCategory } from '~/types'

const props = defineProps<{
  mobile: boolean
  containerClass?: string
  suggestiveProducts?: ProductLiteWithCategory[]
}>()

const emit = defineEmits(['click-navigate', 'clear-cart', 'product-action'])

const appStore = useAppStore()

const { t } = useLocale()
const dialog = useDialog()
const { trackAddToCart, trackRemoveFromCart } = useGTM()

const limitProductOrder = appStore.ecommerceConfig?.limitProductOrder || 10

const deleteProduct = async (cartIndex: number) => {
  const isPromoProduct = !!appStore.cart.products?.[cartIndex]?.isPromo
  const alertKey = `shoppingCart.alerts.${isPromoProduct ? 'deletePromotedConfirmation' : 'delete'}`

  const result = await showDialogAlert({
    dialog,
    data: {
      title: t(`${alertKey}.title`),
      body: t(`${alertKey}.body`),
      btnSecondary: t(`${alertKey}.cancel`),
      btnPrimary: t(`${alertKey}.accept`),
      btnPrimaryClass: 'btn-danger',
    },
  })
  if (result) {
    // Trying to remove a promoted product, if continue will remove promoted products and promo
    if (isPromoProduct) {
      // Remove promotion by id
      const promoProductsId =
        appStore.appliedPromotions?.find(promo => promo.catalogProducts.length)?.promotionId ||
        undefined
      await appStore.setPromotion({ promo: undefined, promotionIdToRemove: promoProductsId })
    } else {
      await appStore.removeCartProduct(cartIndex)
      emit('product-action')
    }
  }
}

const updateCartProductUnits = ({
  unit,
  index,
  isAdd,
}: {
  unit: number
  index: number
  isAdd: boolean
}) => {
  const cartProduct = appStore.cart.products?.[index]
  if (cartProduct) {
    cartProduct.unit = 1
    if (isAdd) trackAddToCart([cartProduct], undefined)
    else trackRemoveFromCart([cartProduct])
  }
  appStore.updateCartProductUnits(index, unit)
}

const onClickNavigate = (cartIndex: number) => {
  // set appropiate productType if any on cartProduct for editing that product!
  const cartProductProductType = appStore.cart.products?.[cartIndex]?.productType as ProductType
  appStore.setProductTypeOnDetail(cartProductProductType)
  emit('click-navigate')
}
</script>

<template>
  <div class="my-4 w-full md:my-0" :class="containerClass || ''">
    <div
      v-if="!appStore.cart.products.length"
      class="mx-auto mb-5 flex w-full flex-col items-center justify-center gap-4 md:mb-10 md:w-[25vw]"
    >
      <img class="h-auto w-full md:w-3/4" src="/images/icons/empty-bag.svg" alt="empty" />
      <h4 class="text-center font-bold md:text-lg">{{ t('shoppingCart.empty.title') }}</h4>
      <p class="text-center text-sm">
        {{ t('shoppingCart.empty.text') }}
      </p>
    </div>
    <section v-else>
      <div class="my-1 mb-4 flex flex-row items-center justify-between md:mb-6">
        <h3 class="text-2xl font-bold md:text-lg">{{ t('shoppingCart.products') }}</h3>
        <a
          v-if="!mobile"
          class="cursor-pointer text-mcd-link underline"
          @click.prevent.stop="emit('clear-cart')"
        >
          {{ t('shoppingCart.actions.clear') }}
        </a>
      </div>
      <CartProductItem
        v-for="(product, index) in appStore.cart.products"
        :key="`${product.identifier}${index}`"
        :product="product"
        :cart-index="index"
        :limit-product-order="limitProductOrder"
        :mobile="mobile"
        @delete-product="deleteProduct"
        @update-units="updateCartProductUnits"
        @click-navigate="onClickNavigate(index)"
      />

      <CartSuggestiveCarrousel
        v-if="props.suggestiveProducts"
        :suggestive-products="props.suggestiveProducts"
        @product-action="() => emit('product-action')"
      />
    </section>
  </div>
</template>
